import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import ContactForm from "@/Components/Universal/ContactForm";
import TopHero from "@/Components/Universal/TopHero";
import FrontLayout from "@/Layouts/FrontLayout";
import { ApartmentModel } from "@/types/models/apartment";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Head, Link } from "@inertiajs/react";

import { HighlightedApartment } from "@/Components/Homepage/HighlightedApartment";
import { ShowApartmentBanner } from "@/Components/Apartments/ShowApartmentBanner";
import { WhyVisitSection } from "@/Components/Apartments/WhyVisitSection";
import { ShowApartmentGallery } from "@/Components/Apartments/ShowApartmentGallery";
import { DuringVisitSection } from "@/Components/Apartments/DuringVisitSection";
import { StandardAmenitiesSection } from "@/Components/Apartments/StandardAmenitiesSection";

const Details = ({ apartment }: { apartment: ApartmentModel }) => {
    const resolveStatus = (status: string) => {
        let resolvedStatus = <span></span>;

        switch (status) {
            case "Wolne":
                resolvedStatus = (
                    <span className="text-[#1E7200] font-extrabold">
                        Dostępne
                    </span>
                );
                break;
            case "Zarezerwowane":
                resolvedStatus = (
                    <span className="text-i-red font-extrabold">
                        Zarezerwowane
                    </span>
                );
                break;
            case "WstepnieZarezerwowany":
            case "Wstępnie Zarezerwowany":
                resolvedStatus = (
                    <span className="text-i-red font-extrabold">
                        Zarezerwowane
                    </span>
                );
                break;
            case "Sprzedane":
                resolvedStatus = (
                    <span className="text-black font-extrabold">Sprzedane</span>
                );
                break;
        }

        return resolvedStatus;
    };

    const getFormat = (filename: string) => {
        return filename.split(".").pop();
    };

    const formatArea = (value?: number | string | null) => {
        const num = Number(value);

        if (!Number.isFinite(num) || num <= 0) {
            return null;
        }

        return new Intl.NumberFormat("pl-PL", {
            maximumFractionDigits: 2,
        }).format(num);
    };

    return (
        <>
            <Head title="Osiedle Kusocińskiego">
                <meta name="description" content="Osiedle Kusocińskiego" />
            </Head>
            <FrontLayout>
                <TopHero
                    image="apartments-breadcrumbs-bg.webp"
                    text={`Mieszkania`}
                />
                <Container className="mt-6 maxMd:mt-14">
                    <Row className="items-center">
                        <Column xl={1} lg={1} md={2} className="maxMd:order-2">
                            <div id="section-details" className="floors w-full text-center border-r-[1px] maxMd:flex-wrap maxMd:border-r-0 maxMd:flex border-secondary pb-6">
                                <p className="maxMd:w-full maxMd:py-0 maxMd:text-left text-center text-p leading-p text-text font-extrabold pt-4 pb-2 mb-4">
                                    Piętro
                                </p>
                                {Array.from({
                                    length:
                                        Math.max(10, Number(apartment.floor) || 0) + 1,
                                }).map((_, i, floors) => (
                                    <p
                                        key={i}
                                        className={`text-center maxMd:mx-[13px] ${i === 0 ? "maxMd:ml-0" : ""} ${i === floors.length - 1 ? "maxMd:mr-0" : ""} text-p leading-p text-text mb-4 ${i === Number(apartment.floor) ? "!text-primary border-[1px] rounded-full border-primary inline-block w-8 h-8 leading-[27.5px] shadow-lg" : ""}`}
                                    >
                                        {i}
                                    </p>
                                ))}
                                <div className="maxMd:block hidden border-b-[1px] border-secondary w-full my-4"></div>
                                {apartment.get_attachment && (
                                    <picture>
                                        <source
                                            srcSet={`${apartment.get_attachment?.layout.replace(/\.[^.]+$/, "")}.webp`}
                                            type="image/webp"
                                        />
                                        <source
                                            srcSet={`${apartment.get_attachment?.layout.replace(/\.[^.]+$/, "")}.${getFormat(apartment.get_attachment?.layout)}`}
                                            type={`image/${getFormat(apartment.get_attachment?.layout)}`}
                                        />
                                        <img
                                            className="maxMd:block hidden w-full h-full object-cover object-center"
                                            src={
                                                apartment.get_attachment?.layout
                                            }
                                            alt={`Mieszkanie ${apartment.name}`}
                                        />
                                    </picture>
                                )}
                                {apartment.get_attachment === null && (
                                    <Icon
                                        icon="clarity:image-line"
                                        width={220}
                                        className="mx-auto text-secondary maxMd:block hidden"
                                    />
                                )}
                                <div className="maxMd:block hidden w-full mt-8">
                                    <a
                                        href="#contact-form"
                                        className="button-primary mb-2 w-full border-primary border-[2px]"
                                    >
                                        Zapytaj o mieszkanie
                                    </a>
                                    {apartment.get_attachment && (
                                        <a
                                            href={
                                                apartment.get_attachment
                                                    .apartment_card
                                            }
                                            target="_blank"
                                            className="button-outline w-full border-primary border-[2px] block text-center"
                                        >
                                            Pobierz rzut
                                        </a>
                                    )}
                                    <a
                                        href="tel:664087645"
                                        target="_blank"
                                        className="button-outline mt-2 w-full border-primary border-[2px] block text-center"
                                    >
                                        Zadzwoń do nas
                                    </a>
                                </div>
                            </div>
                        </Column>
                        <Column
                            xl={11}
                            lg={11}
                            md={10}
                            className="maxMd:order-1"
                        >
                            <Row>
                                <Column
                                    xl={4}
                                    lg={4}
                                    md={6}
                                    className="self-center"
                                >
                                    <div className="lg:pl-6 lg:pr-16 px-0 py-8 flex flex-col gap-14">
                                        <div className="flex flex-col gap-4">
                                            <h2 className="text-h3 leading-h3 uppercase tracking-[0.8px] flex flex-wrap gap-x-4">
                                                <span className="text-text">
                                                    Mieszkanie
                                                </span>
                                                <span className="text-primary">
                                                    {apartment.name}
                                                </span>
                                            </h2>
                                            {apartment.get_attachment
                                                ?.apartment_card && (
                                                    <a
                                                        href={
                                                            apartment
                                                                .get_attachment
                                                                .apartment_card
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="button-small text-primary block w-full text-center"
                                                    >
                                                        Pobierz rzut
                                                    </a>
                                                )}
                                        </div>

                                        <div className="flex flex-col gap-2 text-h4 leading-h4 tracking-[0.6px] uppercase">
                                            <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                Liczba pokoi:
                                                <span className="text-primary">
                                                    {apartment.numberofrooms}
                                                </span>
                                            </p>
                                            <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                Powierzchnia:
                                                <span className="text-primary">
                                                    {formatArea(apartment.area)}
                                                    m
                                                    <sup>2</sup>
                                                </span>
                                            </p>
                                            {apartment.status !==
                                                "Sprzedane" && (
                                                    <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                        Cena:
                                                        <span className="text-primary">
                                                            {new Intl.NumberFormat(
                                                                "pl-PL",
                                                            ).format(
                                                                apartment.priceforapartment,
                                                            )}{" "}
                                                            PLN
                                                        </span>
                                                    </p>
                                                )}
                                            <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                Status:
                                                {resolveStatus(
                                                    apartment.status,
                                                )}
                                            </p>
                                            {formatArea(
                                                apartment.balconysize,
                                            ) && (
                                                    <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                        Balkon:
                                                        <span className="text-primary">
                                                            {formatArea(
                                                                apartment.balconysize,
                                                            )}
                                                            m
                                                            <sup>2</sup>
                                                        </span>
                                                    </p>
                                                )}
                                            {formatArea(
                                                apartment.loggiasize,
                                            ) && (
                                                    <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                        Loggia:
                                                        <span className="text-primary">
                                                            {formatArea(
                                                                apartment.loggiasize,
                                                            )}
                                                            m
                                                            <sup>2</sup>
                                                        </span>
                                                    </p>
                                                )}
                                            {formatArea(
                                                apartment.terracesize,
                                            ) && (
                                                    <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                        Taras:
                                                        <span className="text-primary">
                                                            {formatArea(
                                                                apartment.terracesize,
                                                            )}
                                                            m
                                                            <sup>2</sup>
                                                        </span>
                                                    </p>
                                                )}
                                            {formatArea(
                                                apartment.gardensize,
                                            ) && (
                                                    <p className="flex gap-2 items-center text-[#2F2F2F]">
                                                        Ogród:
                                                        <span className="text-primary">
                                                            {formatArea(
                                                                apartment.gardensize,
                                                            )}
                                                            m
                                                            <sup>2</sup>
                                                        </span>
                                                    </p>
                                                )}
                                        </div>

                                        <div className="flex flex-col gap-4 w-full maxMd:hidden">
                                            <a
                                                href="#contact-form"
                                                className="button-small bg-primary text-white block text-center w-full"
                                            >
                                                Zapytaj o mieszkanie
                                            </a>
                                            <a
                                                href="tel:664087645"
                                                className="button-small text-primary w-full block text-center"
                                            >
                                                Zadzwoń do nas
                                            </a>
                                            <Link
                                                href={route(
                                                    "frontend.apartments.yslab.details",
                                                    {
                                                        apartmentName:
                                                            apartment.name.replace(
                                                                "/",
                                                                "_",
                                                            ),
                                                    },
                                                )}
                                                className="flex items-center justify-center gap-x-2 py-3 px-4 text-[14px] leading-4 tracking-[0.35px] font-extrabold uppercase text-primary text-center w-full"
                                            >
                                                Więcej szczegółów
                                                <Icon
                                                    icon={`basil:arrow-right-outline`}
                                                    width={24}
                                                />
                                            </Link>
                                        </div>
                                    </div>
                                </Column>
                                <Column
                                    xl={8}
                                    lg={8}
                                    md={6}
                                    className="maxMd:hidden"
                                >
                                    {apartment.get_attachment && (
                                        <picture>
                                            <source
                                                srcSet={`${apartment.get_attachment?.layout.replace(/\.[^.]+$/, "")}.webp`}
                                                type="image/webp"
                                            />
                                            <source
                                                srcSet={`${apartment.get_attachment?.layout.replace(/\.[^.]+$/, "")}.${getFormat(apartment.get_attachment?.layout)}`}
                                                type={`image/${getFormat(apartment.get_attachment?.layout)}`}
                                            />
                                            <img
                                                className="w-full h-full object-cover object-center"
                                                src={
                                                    apartment.get_attachment
                                                        ?.layout
                                                }
                                                alt={`Mieszkanie ${apartment.name}`}
                                            />
                                        </picture>
                                    )}
                                    {apartment.get_attachment === null && (
                                        <Icon
                                            icon="clarity:image-line"
                                            width={220}
                                            className="mx-auto text-secondary"
                                        />
                                    )}
                                </Column>
                            </Row>
                        </Column>
                    </Row>
                </Container>

                <Container>
                    <Row>
                        <Column>
                            <Link
                                href={route("frontend.apartments")}
                                className="button-outline max-w-[350px] w-full mx-auto mb-8"
                            >
                                Powrót do wyszukiwarki
                                <Icon icon="simple-line-icons:arrow-up" />
                            </Link>
                        </Column>
                    </Row>
                </Container>


                {apartment.name == 'A/02' && (
                    <>
                        <ShowApartmentBanner />
                        <WhyVisitSection />
                        <ShowApartmentGallery />
                        <DuringVisitSection />
                        <StandardAmenitiesSection apartment={apartment} />
                    </>
                )}




                {apartment.name != 'A/02' &&
                    <HighlightedApartment />
                }
                <ContactForm
                    smallerMargin
                    apartmentId={apartment.api_id}
                    apartmentName={`${apartment.building_number}/${apartment.flat_number}`}
                />
            </FrontLayout>
        </>
    );
};

export default Details;
